import { useMutation, useQuery } from '@apollo/client';
import { ListAltOutlined } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Permission } from '@procureai/permissions';
import { Restricted, usePermissions } from '@procureai/permissions-ui';
import {
  useConfirmationDialog,
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  useCustomSnackbar,
} from '@procureai/ui-mui';
import { isEmpty } from 'ramda';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { TsrctContractDemandStatus } from '@/generated/gql/graphql';
import { I18nNamespace, NegotiationDomainType } from '~/common/types';
import {
  ColumnSelector,
  DataGridProStyled,
  DataGridWidgetHeader,
  DensityButton,
  ResetRemoteStateButton,
  useDataGridRemoteState,
} from '~/components/DataGridCustom';
import { HoverMenuProvider } from '~/components/HoverMenu';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { useOpenClose } from '~/hooks';
import { RouteId } from '~/routes';
import { BatchSupplierContact } from '~/routes/IntakeDetail/components/SupplierContactForm/SupplierContactForm.schema';

import { BatchAddSuppliersDialog } from './components/BatchAddSuppliersDialog/BatchAddSuppliersDialog';
import { useContractDemandCasesDataGridConfig } from './ContractDemandCaseListWidget.dataGrid';
import {
  CONTRACT_DEMAND_ADD_CASES_MUTATION,
  CONTRACT_DEMAND_CASES_QUERY,
  RELEASE_SOURCING_CASE_MUTATION,
} from './ContractDemandCaseListWidget.gql';
import { useQualifiedSuppliers, useReleasableCases } from './ContractDemandCaseListWidget.hooks';

const domain = NegotiationDomainType.CONTRACT_DEMAND_CASES;

export const ContractDemandCaseListWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractDemandCaseListWidget' });
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const config = useContractDemandCasesDataGridConfig();
  const { onError } = useCustomSnackbar();
  const { confirm } = useConfirmationDialog();
  const { hasPermission } = usePermissions();

  const {
    loading: loadingRemoteState,
    gridProps,
    onDensityChange,
    onRemoteStateReset,
  } = useDataGridRemoteState({
    apiRef,
    config,
    domain,
  });

  const { data, loading, previousData } = useQuery(CONTRACT_DEMAND_CASES_QUERY, {
    variables: { id: entityId },
    skip: !entityId,
    fetchPolicy: 'cache-and-network',
  });

  const demand = (data || previousData)?.tesseract.contractDemand;

  const [addSourcingCases, { loading: addCaseLoading }] = useMutation(CONTRACT_DEMAND_ADD_CASES_MUTATION, {
    refetchQueries: [
      {
        query: CONTRACT_DEMAND_CASES_QUERY,
        variables: { id: entityId },
      },
    ],
  });

  const [releaseSourcingCase, { loading: addReleaseLoading }] = useMutation(RELEASE_SOURCING_CASE_MUTATION, {
    refetchQueries: [
      {
        query: CONTRACT_DEMAND_CASES_QUERY,
        variables: { id: entityId },
      },
    ],
  });

  const isDisabled = demand?.status === TsrctContractDemandStatus.AWARDED || loading;

  const disableAddingSuppliers = isDisabled || !hasPermission(Permission.SourcingCaseManualCreate);

  const [{ open: dialogOpen, onClose: dialogOnClose }, openDialog] = useOpenClose();

  const { qualifiedSuppliers, existingSupplierIdsSet } = useQualifiedSuppliers(demand);
  const [shouldUseQualifiedSuppliers, setShouldUseQualifiedSuppliers] = useState(false);

  const releasableCases = useReleasableCases(demand);

  const handleAddSuppliers = async (formData: BatchSupplierContact) => {
    const hasExistingSupplier = formData.suppliers.some((item) =>
      existingSupplierIdsSet.has(item.supplier?.internalId),
    );

    if (hasExistingSupplier) {
      onError(t('errors.supplierAlreadyExists'));
      return;
    }

    const suppliers = formData.suppliers
      .filter((item) => {
        const supplierId = item.supplier?.internalId;
        if (!supplierId || !item.supplier?.contact) {
          return false;
        }

        return true;
      })
      .map((item) => ({
        id: item.supplier.internalId,
        name: item.supplier.name,
        contact: {
          email: item.supplier.contact.email,
          firstName: item.supplier.contact.firstName,
          lastName: item.supplier.contact.lastName,
          language: item.supplier.contact.language as any,
          contactId: item.supplier.contact.internalId,
        },
      }));

    if (suppliers.length > 0) {
      await addSourcingCases({
        variables: {
          id: entityId,
          data: {
            suppliers,
          },
        },
      });
    }
  };

  const handleReleaseAll = async () => {
    for (const sourcingCase of releasableCases) {
      await releaseSourcingCase({
        variables: { id: sourcingCase.id },
      });
    }
  };

  const skip = loadingRemoteState || loading;

  const sourcingCases = data?.tesseract.contractDemand?.sourcingCases || [];

  return (
    <>
      <Widget>
        <WidgetHeader
          title={t('title')}
          icon={<WidgetIcon icon={<ListAltOutlined fontSize="small" />} />}
          actionsRight={
            <Stack direction="row" spacing={1}>
              <Button
                disabled={disableAddingSuppliers}
                onClick={() => {
                  openDialog();
                  setShouldUseQualifiedSuppliers(false);
                }}
                variant="outlined"
              >
                {t('button.addSuppliers')}
              </Button>
              <Button
                disabled={disableAddingSuppliers || qualifiedSuppliers.length < 1}
                onClick={() => {
                  openDialog();
                  setShouldUseQualifiedSuppliers(true);
                }}
                loading={addCaseLoading}
                variant="contained"
                color="primary"
              >
                {t('button.addQualifiedSuppliers', { count: qualifiedSuppliers.length })}
              </Button>
              <Restricted needs={{ permission: Permission.CaseRelease }}>
                <Button
                  disabled={isDisabled || isEmpty(releasableCases) || !hasPermission(Permission.SourcingCaseRelease)}
                  loading={addReleaseLoading}
                  onClick={confirm(handleReleaseAll, {
                    title: t('acceptDialog.title'),
                    maxWidth: 'md',
                    message: t('acceptDialog.description'),
                    confirm: {
                      children: t('acceptDialog.apply'),
                      color: 'primary',
                    },
                    cancel: {
                      children: t('acceptDialog.cancel'),
                    },
                  })}
                  variant="contained"
                  color="primary"
                >
                  {t('button.releaseAll', { count: releasableCases.length })}
                </Button>
              </Restricted>
            </Stack>
          }
        />
        <WidgetContent>
          {skip ? null : (
            <div>
              <DataGridWidgetHeader
                right={
                  <Stack direction="row">
                    <DensityButton density={gridProps.density} onDensityChange={onDensityChange} />
                    <ColumnSelector
                      columns={gridProps.columns}
                      columnVisibilityModel={gridProps.columnVisibilityModel}
                      onColumnVisibilityModelChange={gridProps.onColumnVisibilityModelChange}
                    />
                    <ResetRemoteStateButton onRemoteStateReset={onRemoteStateReset} />
                  </Stack>
                }
              />
              <HoverMenuProvider>
                <DataGridProStyled
                  {...config}
                  apiRef={apiRef}
                  autoHeight
                  hideFooter
                  disableColumnMenu
                  onRowClick={({ row }) => {
                    navigate(RouteId.sourcingCaseDetail(row.id));
                  }}
                  loading={loading}
                  rows={sourcingCases}
                  {...gridProps}
                />
              </HoverMenuProvider>
            </div>
          )}
        </WidgetContent>
      </Widget>

      <BatchAddSuppliersDialog
        open={dialogOpen}
        onClose={dialogOnClose}
        qualifiedSuppliers={shouldUseQualifiedSuppliers ? qualifiedSuppliers : []}
        onSubmit={handleAddSuppliers}
        loading={addCaseLoading}
      />
    </>
  );
};
